import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NODE_CATALOG, assessPlan, weeklyPlanSeries, weightTrajectories } from '@loggy/shared';
import type { BodyProfile } from '@loggy/shared';

import { Alert, Card, Checkbox, Heading, Inline, Skeleton, Slider, Spinner, Stack, Text, cx } from '../design/index.js';
import { fetchBodyProfile, fetchNodeEntries } from '../api.js';
import type { EntriesResolution, OrbitGoal, OrbitItem, SeriesPoint, NodeSummary } from '../api.js';
import { MeasurementChart } from '../charts/MeasurementChart.js';
import {
  clampOffsetDays,
  groupingAfterWeeklyToggle,
  describeSpanDays,
  historySpanDays,
  pointsInWindowWithBleed,
  centreForRange,
  RANGE_STEPS,
  heldSpan,
  narrowestRange,
  rangeSpanForZoom,
  snapRange,
  zoomForRangeSpan,
  resolutionForSpanDays,
  spanDaysForZoom,
  windowFor,
  ZOOM_STEPS,
  zoomForSpanDays,
} from './chartWindow.js';
import type { Grouping } from './chartWindow.js';
import { weeklyChanges } from '../charts/chartMath.js';
import type { WeeklyChange } from '../charts/chartMath.js';
import { buildGoalReadout } from './goalReadout.js';
import { ChartViewMenu } from './ChartViewMenu.js';
import { PlanAnalysis } from './PlanAnalysis.js';
import { LifeEvents } from './LifeEvents.js';
import { FoodLibrary } from './FoodLibrary.js';
import { navigateToSettings } from './useHashRoute.js';
import { useEventOverlay } from './useEventOverlay.js';
import styles from './NodeDetail.module.css';

/** The readings list always looks at a fixed recent window regardless of the
 *  chart's own range, falling back to the node's whole history only when
 *  that window happens to be empty (a node with month-long gaps, opened
 *  right after a gap). */
const RECENT_READINGS_WINDOW_DAYS = 30;
const RECENT_READINGS_LIMIT = 8;

function sourceLabel(source: SeriesPoint['source']): string {
  switch (source) {
    case 'withings':
      return 'Withings Body Scan';
    case 'manual':
      return 'Entered by hand';
    case 'import':
      return 'Imported';
    default:
      return 'Unknown source';
  }
}

function formatReadingWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  // en-GB for day-first ordering, as the chart above this list already uses.
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

async function loadRecentReadings(nodeId: string): Promise<{ readings: SeriesPoint[]; truncated: boolean }> {
  const recentFrom = new Date();
  recentFrom.setDate(recentFrom.getDate() - RECENT_READINGS_WINDOW_DAYS);
  let response = await fetchNodeEntries(nodeId, { from: recentFrom.toISOString(), resolution: 'raw' });
  if (response.entries.length === 0) {
    // Nothing in the recent window — a node with sparse readings (e.g. a
    // blood pressure cuff used a few times a year) would otherwise look like
    // it has never been measured at all.
    response = await fetchNodeEntries(nodeId, { resolution: 'raw' });
  }
  const readings = [...response.entries]
    .sort((a, b) => b.recordedAt.localeCompare(a.recordedAt))
    .slice(0, RECENT_READINGS_LIMIT);
  return { readings, truncated: response.truncated };
}

type DetailState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'empty' }
  | {
      kind: 'ready';
      series: SeriesPoint[];
      aggregated: boolean;
      /** Which bucket the series on screen actually holds — carried in the
       *  state, not read from `resolution`, because the two disagree for as
       *  long as a new grouping is in flight and the chart would otherwise
       *  label daily points as weeks. */
      bucket?: 'daily' | 'weekly';
      chartTruncated: boolean;
      readings: SeriesPoint[];
      readingsTruncated: boolean;
    };

/** A `NodeSummary` known to be a metric with a real unit and precision. Only
 *  this shape may reach the hero figure, the chart or the readings list —
 *  `unit`/`precision` are `null` for a `collection` or `document` node, and
 *  narrowing once here (rather than `?? 0` at each call site) means a
 *  missing precision can never silently round a real measurement to a
 *  whole number. */
type MetricNodeSummary = NodeSummary & { kind: 'metric'; unit: string; precision: number };

function isMetricNode(node: NodeSummary): node is MetricNodeSummary {
  return node.kind === 'metric' && node.unit !== null && node.precision !== null;
}

/**
 * What a goal still asks of you, under the chart that shows the history.
 *
 * The chart already draws the target as a dashed line; this says the two
 * things a line cannot: how far away it is, and what that distance costs per
 * week if the deadline is to be met. The wording lives in `buildGoalReadout`
 * so every state it distinguishes can be tested without signing in and
 * arranging that state by hand.
 */
function GoalReadout({ node, goal }: { node: MetricNodeSummary; goal: OrbitGoal }) {
  const { status, heading, detail } = buildGoalReadout({
    nodeTitle: node.title,
    unit: node.unit,
    precision: node.precision,
    goal: {
      title: goal.title,
      targetValue: goal.targetValue,
      baselineValue: goal.baselineValue,
      targetDate: goal.targetDate,
      direction: goal.direction,
      achieved: goal.progress.achieved,
      currentValue: goal.progress.currentValue,
    },
  });

  return (
    <div className={styles.goalReadout} data-testid="node-detail-goal">
      <span aria-hidden="true" className={cx(styles.goalSwatch, styles[status])} />
      <Stack gap={1}>
        <Text size="xs" color="muted" className={styles.label}>{heading}</Text>
        <Text size="sm" color="secondary">{detail}</Text>
      </Stack>
    </div>
  );
}

/**
 * Builds what NodeDetail needs from an orbit item, for a node reached while
 * viewing someone else's dashboard. `useNodes` only ever holds the caller's
 * OWN nodes, so a node merely shared with the caller is never in it — but the
 * orbit item already fetched to draw the ring (`useOrbit`) carries every
 * field `NodeSummary` does (`nodeId` becomes `id`). A mapping into the
 * existing prop shape, rather than a second prop type on NodeDetail, so
 * opening a node stays one component whichever dashboard it belongs to; the
 * chart and history need no change either way, since
 * `GET /api/nodes/:id/entries` already works for a node shared with the
 * caller.
 */
export function orbitItemToNodeSummary(item: OrbitItem): NodeSummary {
  return {
    id: item.nodeId,
    typeKey: item.typeKey,
    title: item.title,
    kind: item.kind,
    unit: item.unit,
    precision: item.precision,
    latestValue: item.latestValue,
    latestRecordedAt: item.latestRecordedAt,
    entryCount: item.entryCount,
    source: item.source,
  };
}

/** A window edge, short enough to be a control's spoken value. */
function formatWindowEdge(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString(undefined, { timeZone: 'UTC', year: 'numeric', month: 'short' });
}

export interface NodeDetailProps {
  node: NodeSummary;
  /**
   * The node's active goal, or null when it is merely watched.
   *
   * Passed down rather than fetched: `Dashboard` already holds the orbit, and
   * the orbit already carries every node's active goal with its progress
   * computed server-side. Fetching it again here would mean a second request
   * that can disagree with the one the rest of the page is drawn from.
   */
  goal?: OrbitGoal | null;
  /**
   * The node holding this dashboard's life events, when there is one the
   * caller may actually read.
   *
   * Resolved by `Dashboard` from the orbit it is already showing, which is
   * what makes the sharing rule hold without a check here: a viewer's orbit
   * contains only the nodes granted to them, so an events node that was never
   * shared is simply absent and there is nothing to offer.
   */
  eventsNodeId?: string | null;
  /** True when this is somebody else's dashboard. */
  readOnly?: boolean;
  onBack: () => void;
}

export function NodeDetail({
  node,
  goal = null,
  eventsNodeId = null,
  readOnly = false,
  onBack,
}: NodeDetailProps) {
  /**
   * How much time is on screen, and how far back from the latest reading it
   * sits. Four buttons offering 30 days, 90 days, 6 months and all of it were
   * a coarse way to look at seven years, so the owner asked for a continuous
   * control instead.
   *
   * `spanDays` rather than a slider position is the state, because the slider
   * needs to know the whole history to place itself and the history is not
   * known until the readings arrive. Six months is where a node opens, which
   * is where it opened before this existed.
   */
  const [spanDays, setSpanDays] = useState(182);
  const [offsetDays, setOffsetDays] = useState(0);
  const [state, setState] = useState<DetailState>(
    node.kind === 'metric' && node.entryCount > 0 ? { kind: 'loading' } : { kind: 'empty' },
  );

  const catalogEntry = useMemo(
    () => NODE_CATALOG.find((entry) => entry.key === node.typeKey),
    [node.typeKey],
  );
  /**
   * Which way this reader wants this number to go — taken from their own
   * goal, never from the catalog.
   *
   * The catalog calls body fat 'lower-is-better' and muscle mass
   * 'higher-is-better', and colouring a trend by that tells someone recovering
   * from an eating disorder that the number moving against them is the good
   * news. The app shows what the number did; whether that is progress is a
   * question only the reader has answered, and only by setting a goal.
   */
  const direction = goal?.direction ?? 'neutral';
  const suggestedAxis = catalogEntry?.metric?.suggestedAxis;
  // A weekly bucket for a 'sum' node (custom.number) is already a total for
  // that week, not a level that moved within it — "gained or lost inside the
  // week" describes a mean-aggregated node, not a summed one. The line still
  // works for a summed node; only the change band is withheld.
  const aggregation = catalogEntry?.metric?.aggregation;

  /**
   * Grouping now follows the zoom rather than being chosen separately.
   *
   * It used to be derived from the range, then the owner asked for it to be
   * an explicit choice of its own, and has now asked for it back — knowing
   * that is a reversal. The reasoning that won: a year of individual weigh-ins
   * is a smear and a fortnight of weekly averages is four points and no shape,
   * so every combination worth having is one the zoom can pick.
   *
   * `grouping` is the override on top of that, because the zoom picking well
   * on average is not the same as it picking what this reader wants right
   * now: weekly averages over a single month are a legitimate thing to ask
   * for, and so is every individual reading across a year. 'auto' means the
   * zoom decides, and is where it starts and stays until the box is touched.
   */
  const autoResolution = resolutionForSpanDays(spanDays);
  const [grouping, setGrouping] = useState<Grouping>('auto');
  const resolution = grouping === 'auto' ? autoResolution : grouping;

  /**
   * What has already been fetched, per grouping.
   *
   * There are only three, and a node's whole history at any one of them is a
   * few hundred points. Keeping them means crossing a zoom threshold a second
   * time costs nothing at all — the readings are simply already there, which
   * is what the owner asked for.
   */
/**
 * The weight plan: a second chart and an analysis, for a metric node that has
 * a goal with a deadline and a baseline to measure from.
 *
 * Everything it needs is computed in `@loggy/shared` from three inputs: the
 * raw readings since the goal started, the goal itself, and the optional body
 * profile. Nothing is computed on the server, so a viewer of a shared node
 * simply gets `profile: null` back from the API and the profile-derived
 * figures never exist on their machine to begin with.
 */
function usePlan(node: NodeSummary, goal: OrbitGoal | null | undefined) {
  const eligible =
    goal !== null &&
    goal !== undefined &&
    goal.targetDate !== null &&
    goal.baselineValue !== null &&
    node.kind === 'metric';

  const [planReadings, setPlanReadings] = useState<SeriesPoint[] | null>(null);
  const [profile, setProfile] = useState<BodyProfile | null>(null);

  useEffect(() => {
    if (!eligible || !goal) {
      setPlanReadings(null);
      return;
    }
    let cancelled = false;
    // RAW readings from the goal's own start, not the zoomed window: the plan
    // is about the whole goal, and it must not change when the reader moves a
    // slider that is only about looking.
    fetchNodeEntries(node.id, { from: goal.startDate, resolution: 'raw' })
      .then((response) => {
        if (!cancelled) setPlanReadings(response.entries);
      })
      .catch(() => {
        // A failed plan fetch is not worth an error card over the chart the
        // reader actually came for. The analysis simply does not appear.
        if (!cancelled) setPlanReadings(null);
      });
    return () => {
      cancelled = true;
    };
  }, [eligible, goal, node.id, node.entryCount]);

  useEffect(() => {
    if (!eligible) return;
    let cancelled = false;
    fetchBodyProfile()
      .then((result) => {
        if (!cancelled) setProfile(result);
      })
      .catch(() => {
        // Not having one is the ordinary case, and so is being someone else's
        // guest. Either way the band is simply absent.
        if (!cancelled) setProfile(null);
      });
    return () => {
      cancelled = true;
    };
  }, [eligible]);

  return useMemo(() => {
    if (!eligible || !goal || planReadings === null) return null;
    const goalInput = {
      baselineValue: goal.baselineValue,
      targetValue: goal.targetValue,
      startDate: goal.startDate,
      targetDate: goal.targetDate,
      direction: goal.direction,
    };
    const now = new Date();
    const assessment = assessPlan({ readings: planReadings, goal: goalInput, profile, now });
    return {
      assessment,
      points: weeklyPlanSeries({ readings: planReadings, goal: goalInput, now }),
      trajectories: weightTrajectories({
        readings: planReadings,
        goal: goalInput,
        assessment,
        profile,
        now,
      }),
      profile,
      // Which way THEY chose to go, from their own baseline and target —
      // never the catalog's opinion of the measurement.
      goingDown: (goal.baselineValue ?? 0) > goal.targetValue,
    };
  }, [eligible, goal, planReadings, profile]);
}

  const seriesCacheRef = useRef<Map<EntriesResolution, SeriesPoint[]>>(new Map());

  /** Whether there are readings on screen right now. Read by the effect above
   *  to decide between using the cache and fetching — a question it cannot ask
   *  of `state` without taking a dependency on it and re-running on every
   *  change it makes itself. Deliberately not asked inside a setState updater:
   *  React may call one twice, and an updater must have no effects. */
  const hasReadingsRef = useRef(false);

  // A different node's readings are a different cache.
  useEffect(() => {
    seriesCacheRef.current = new Map();
    hasReadingsRef.current = false;
  }, [node.id]);

  useEffect(() => {
    if (node.kind !== 'metric' || node.entryCount === 0) {
      setState({ kind: 'empty' });
      return;
    }

    let cancelled = false;
    // Only on the way in. Once there are readings on screen they stay there
    // while a different grouping loads underneath — dropping back to a
    // loading card mid-zoom replaces the chart with a spinner and shoves
    // everything below it up the page, which is a jump in the middle of a
    // drag. The data already shown is still true; it is only grouped
    // differently from what is on its way.
    setState((prev) => (prev.kind === 'ready' ? prev : { kind: 'loading' }));

    // The WHOLE history at this resolution, once. The largest node holds a
    // few hundred points, so zooming and panning are then slicing already-held
    // data rather than a request per gesture — which is what makes a drag
    // follow the finger instead of stuttering behind it.
    // Already fetched at this grouping: use it and ask for nothing. Crossing a
    // zoom threshold back and forth then costs no request at all, which is
    // what makes the slider feel like it is moving through data the page
    // already has rather than through data it keeps going to get.
    //
    // The readings list below the chart is untouched by this: it is always raw
    // and always the same recent window, so it does not depend on the
    // grouping and is carried over as it stands.
    const cached = seriesCacheRef.current.get(resolution);
    if (cached && hasReadingsRef.current) {
      setState((prev) =>
        prev.kind === 'ready'
          ? {
              ...prev,
              series: cached,
              aggregated: resolution !== 'raw',
              bucket: resolution === 'raw' ? undefined : resolution,
            }
          : prev,
      );
      return;
    }

    Promise.all([
      fetchNodeEntries(node.id, { resolution }),
      loadRecentReadings(node.id),
    ])
      .then(([chart, recent]) => {
        if (cancelled) return;
        if (chart.entries.length === 0 && recent.readings.length === 0) {
          setState({ kind: 'empty' });
          return;
        }
        seriesCacheRef.current.set(resolution, chart.entries);
        hasReadingsRef.current = true;
        setState({
          kind: 'ready',
          series: chart.entries,
          aggregated: chart.aggregated,
          bucket: resolution === 'raw' ? undefined : resolution,
          chartTruncated: chart.truncated,
          readings: recent.readings,
          readingsTruncated: recent.truncated,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Could not load these readings.',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [node.kind, node.id, node.entryCount, resolution]);



  const fullSeries = state.kind === 'ready' ? state.series : [];
  const oldestIso = fullSeries[0]?.recordedAt ?? null;
  const latestIso = fullSeries[fullSeries.length - 1]?.recordedAt ?? null;
  const historyDays = historySpanDays(oldestIso, latestIso);

  const plan = usePlan(node, goal);

  // Clamped on the way out rather than on the way in, so a zoom that widens
  // past what is left behind you pulls the window back to the end of the
  // history instead of leaving it hanging over empty time.
  const clampedOffset = clampOffsetDays(offsetDays, spanDays, oldestIso, latestIso);

  const visibleWindow = useMemo(
    () => (latestIso === null ? null : windowFor(latestIso, spanDays, clampedOffset)),
    [latestIso, spanDays, clampedOffset],
  );

  /**
   * With one reading either side, so the line crosses both edges of the frame
   * rather than beginning at the first reading inside it. The chart clips to
   * its plot, so the two extra points are never seen — only the segments they
   * anchor are.
   */
  const visibleSeries = useMemo(
    () => (visibleWindow === null ? fullSeries : pointsInWindowWithBleed(fullSeries, visibleWindow)),
    [fullSeries, visibleWindow],
  );

  /**
   * The figure and its change, both taken from what is ON SCREEN.
   *
   * They used to be taken from the whole fetched series, which was the same
   * thing back when the range buttons decided what was fetched. The zoom
   * broke that quietly: the whole history is fetched once now and the window
   * is chosen in the browser, so "over this period" came to mean "over all of
   * it" while the reader looked at three months. Scrolled back into 2021 the
   * headline went on reporting today's weight beside a line that ended
   * somewhere else entirely.
   */
  const hero = useMemo(() => {
    if (visibleSeries.length === 0) {
      return { current: node.latestValue, delta: null as number | null };
    }
    const current = visibleSeries[visibleSeries.length - 1]!.value;
    const delta = visibleSeries.length >= 2 ? current - visibleSeries[0]!.value : null;
    return { current, delta };
  }, [visibleSeries, node.latestValue]);

  // Without a goal there is no 'positive' or 'critical' to be had — the number
  // simply moved, and it is shown in the same ink as everything else.
  const deltaTone: 'positive' | 'critical' | 'secondary' =
    hero.delta === null || direction === 'neutral' || hero.delta === 0
      ? 'secondary'
      : (direction === 'lower-is-better') === hero.delta < 0
        ? 'positive'
        : 'critical';

  /**
   * The value axis, chosen rather than inferred.
   *
   * It used to be recomputed from the readings on screen, which made the chart
   * shudder on every pan step — each nudge changed the smallest and largest
   * value a little and the whole line shifted. Snapping it to round numbers
   * helped and did not settle it, so the owner asked for the same treatment
   * the time axis got: a slider, and the reader decides.
   *
   * The HEIGHT is theirs. The CENTRE is held: it stays exactly where it is for
   * as long as the readings fit inside, and moves only when they would not.
   * Pinning it outright was the obvious reading of "no more jumping" and the
   * wrong one — scroll back to a year twenty kilos heavier and the line would
   * simply leave the screen.
   */
  const fullSpan = useMemo(() => {
    if (fullSeries.length === 0) return 0;
    let lo = Infinity;
    let hi = -Infinity;
    for (const point of fullSeries) {
      if (point.value < lo) lo = point.value;
      if (point.value > hi) hi = point.value;
    }
    return Number.isFinite(lo) && Number.isFinite(hi) ? hi - lo : 0;
  }, [fullSeries]);

  /** A collection or document has no precision; the axis maths needs one. */
  const axisPrecision = node.precision ?? 1;

  /**
   * The chosen height of the axis, in the node's own units — not the slider's
   * position.
   *
   * The position means different things at different times: the wide end is
   * the whole history, so it stretches as the history does, and it changes
   * with the grouping too. Storing the position would quietly rescale the
   * reader's choice underneath them. Storing the span keeps "four and a half
   * kilos" meaning four and a half kilos, and the slider is placed from it.
   */
  const [chosenSpan, setChosenSpan] = useState<number | null>(null);
  const [axisCentre, setAxisCentre] = useState<number | null>(null);

  useEffect(() => {
    setAxisCentre(null);
  }, [node.id]);

  /**
   * The height the axis uses when nobody has chosen one — decided once per
   * zoom level, and then left alone.
   *
   * Two attempts before this both chased the readings on screen and both
   * shuddered, the second one worse than the first. An axis that keeps
   * adapting to what is in frame cannot hold still while the frame moves;
   * every threshold for "close enough to leave alone" just relocates the
   * flicker to wherever the readings happen to cross it. The second attempt
   * rounded the height to 1, 2 or 5 times a power of ten and then flipped
   * between two rungs whenever the spread hovered near the boundary — 8.0,
   * 2.5, 6.0, 2.5, 6.0.
   *
   * So it is taken from what is on screen at the moment the zoom changes, and
   * held for as long as that zoom lasts. Scrolling never touches it. Zooming
   * is where a new scale is expected anyway, and the reader who wants a
   * different one has a slider.
   */
  const [autoSpan, setAutoSpan] = useState<number | null>(null);

  /** The latest visible spread, for the effect below to read WITHOUT
   *  re-running every time it changes. */
  const visibleSpreadRef = useRef<number>(0);
  visibleSpreadRef.current = useMemo(() => {
    if (visibleSeries.length === 0) return 0;
    let lo = Infinity;
    let hi = -Infinity;
    for (const point of visibleSeries) {
      if (point.value < lo) lo = point.value;
      if (point.value > hi) hi = point.value;
      if (point.min !== undefined && point.min < lo) lo = point.min;
      if (point.max !== undefined && point.max > hi) hi = point.max;
    }
    return Number.isFinite(lo) && Number.isFinite(hi) ? hi - lo : 0;
  }, [visibleSeries]);

  useEffect(() => {
    const floor = narrowestRange(fullSpan, axisPrecision);
    setAutoSpan(heldSpan(null, visibleSpreadRef.current, floor));
    // Deliberately keyed on the ZOOM and nothing else. Adding the spread here
    // is exactly the mistake that made this shudder: it changes on every pan.
  }, [spanDays, resolution, node.id, fullSpan, axisPrecision]);

  const autoRangeSpan = autoSpan ?? Math.max(fullSpan, 10 * 10 ** -axisPrecision);

  const rangeSpan = chosenSpan;

  useEffect(() => {
    if (visibleSeries.length === 0) return;
    let lo = Infinity;
    let hi = -Infinity;
    for (const point of visibleSeries) {
      if (point.value < lo) lo = point.value;
      if (point.value > hi) hi = point.value;
      if (point.min !== undefined && point.min < lo) lo = point.min;
      if (point.max !== undefined && point.max > hi) hi = point.max;
    }
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return;
    // Tested against the axis actually drawn, not against the raw spread of
    // the readings. Against the spread it re-centred constantly: the spread is
    // narrower than the axis, so the middle kept falling outside a band that
    // was never the real one.
    setAxisCentre((prev) => centreForRange(prev, rangeSpan ?? autoRangeSpan, lo, hi));
  }, [visibleSeries, rangeSpan, autoRangeSpan]);

  /**
   * What the chart is told to cover. Snapped to round numbers so the labels
   * are readable and so the held centre lands on a tick rather than between
   * two.
   */
  const chosenAxis = useMemo(() => {
    if (axisCentre === null) return null;
    const span = rangeSpan ?? autoRangeSpan;
    return snapRange(axisCentre - span / 2, axisCentre + span / 2);
  }, [axisCentre, rangeSpan, autoRangeSpan]);

  /**
   * The catalog's own seed and the chosen axis, whichever is wider.
   *
   * Wider, not narrower: `suggestedAxis` exists so a flat series does not look
   * like a rollercoaster, and honouring it can only ever show more.
   */
  const axisSeed = useMemo(() => {
    if (chosenAxis === null) return suggestedAxis;
    if (suggestedAxis === undefined) return chosenAxis;
    return {
      min: Math.min(chosenAxis.min, suggestedAxis.min),
      max: Math.max(chosenAxis.max, suggestedAxis.max),
    };
  }, [chosenAxis, suggestedAxis]);

  const visibleChanges = useMemo(
    () =>
      resolution === 'weekly' && aggregation === 'mean' ? weeklyChanges(visibleSeries) : undefined,
    [resolution, aggregation, visibleSeries],
  );


  /**
   * The events follow the window the reader is looking at, not the data that
   * happens to fall in it. Deriving this from the first and last visible
   * reading — which is what it did before the zoom existed — would fetch the
   * wrong span across a sparse stretch, and a sparse stretch is exactly where
   * somebody panning back wants to know what was going on.
   */

  // Called before the early returns below, because a hook has to be.
  /**
   * Panning is a real scrollbar rather than a drag.
   *
   * Dragging was what the owner asked for first and what they asked to have
   * replaced after using it: it was fiddly, and worse, it said nothing about
   * where in seven years they had ended up. A scroll container has a thumb
   * whose size and position answer that without being asked, and it brings
   * the trackpad, the wheel, a touch swipe, the keyboard and momentum with
   * it — all of which were hand-written and imperfect before.
   *
   * The trick is the ratio: the track is as many times the width of the
   * visible area as there are windows of history, and the chart inside it is
   * pinned with `position: sticky` so it stays put while the track slides
   * underneath. That needs no measuring — a percentage inside the track
   * resolves against the track, so the CSS divides it straight back down.
   */
  const panRatio = Math.max(1, historyDays / spanDays);
  const maxOffsetDays = Math.max(0, historyDays - spanDays);



  const overlay = useEventOverlay(node.id, eventsNodeId, visibleWindow);

  const backButton = (
    <button type="button" className={styles.backLink} data-testid="node-detail-back" onClick={onBack}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M15 6l-6 6 6 6" />
      </svg>
      Dashboard
    </button>
  );

  // A collection (a family tree) or a document (a research note) has no
  // series to chart at all, and `unit`/`precision` are `null` — narrowed here,
  // once, rather than guarding every `.toFixed` and `node.unit` read below.
  // Building an actual family-tree viewer is out of scope; this is a plain
  // card saying what the node is and that there is nothing to chart yet.
  // A node whose type carries `overlay: 'timeline'` holds events, and events
  // have a screen of their own — this is the one non-metric kind that is not
  // just a card saying there is nothing to chart.
  if (catalogEntry?.overlay === 'timeline') {
    return (
      <Stack gap={6} data-testid="node-detail" className={styles.detail}>
        {backButton}
        <Stack gap={2}>
          <Heading level={2} size="lg">{node.title}</Heading>
          <Text size="sm" color="secondary">{catalogEntry.description}</Text>
        </Stack>
        <LifeEvents nodeId={node.id} readOnly={readOnly} />
      </Stack>
    );
  }

  // A node whose type carries `holds: 'foods'` is the owner's food library —
  // a grocery, not a series to chart, so it gets a screen of its own the same
  // way a timeline node does above.
  if (catalogEntry?.holds === 'foods') {
    return (
      <Stack gap={6} data-testid="node-detail" className={styles.detail}>
        {backButton}
        <Stack gap={2}>
          <Heading level={2} size="lg">{node.title}</Heading>
          <Text size="sm" color="secondary">{catalogEntry.description}</Text>
        </Stack>
        <FoodLibrary nodeId={node.id} readOnly={readOnly} />
      </Stack>
    );
  }

  if (!isMetricNode(node)) {
    return (
      <Stack gap={6} data-testid="node-detail" className={styles.detail}>
        {backButton}
        <Card className={styles.stateCard} data-testid="node-detail-non-metric">
          <Stack gap={2}>
            <Heading level={3} size="lg">{node.title}</Heading>
            <Text size="sm" color="secondary">
              {catalogEntry?.description ?? 'Nothing to chart here yet.'}
            </Text>
            <Text size="sm" color="muted">
              {node.entryCount === 0
                ? 'Nothing here yet.'
                : `${node.entryCount === 1 ? '1 entry' : `${node.entryCount} entries`} — there is nothing to chart yet.`}
            </Text>
          </Stack>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack gap={6} data-testid="node-detail" className={styles.detail}>
      {backButton}

      <Stack gap={4} className={styles.heroRow}>
        <Stack gap={1}>
          <Text size="xs" color="muted" className={styles.label}>{node.title}</Text>
          <Inline gap={4} align="baseline" wrap>
            {/* Keyed on the value so React remounts it when the number
                changes, which is what restarts the animation. The reader
                zooming or panning is choosing a different moment to look at,
                and the figure arriving rather than swapping is what tells
                them it is now describing something else.

                The recipe animates each digit separately, staggered. Dropped:
                at 58px on a page somebody opens every morning that reads as a
                slot machine, and the thing worth noticing is the number, not
                the arrival. */}
            <Text
              key={hero.current ?? 'none'}
              as="span"
              family="figure"
              tabular
              className={styles.heroValue}
            >
              {hero.current === null ? '—' : hero.current.toFixed(node.precision)}
            </Text>
            <Text size="lg" color="muted">{node.unit}</Text>
            {hero.delta !== null && (
              <Text size="sm" color={deltaTone}>
                {hero.delta > 0 ? '+' : ''}
                {hero.delta.toFixed(node.precision)} {node.unit} over this period
              </Text>
            )}
          </Inline>
        </Stack>

        <ChartViewMenu>
        <Slider
          label="How much time"
          valueLabel={describeSpanDays(spanDays)}
          minLabel="Close up"
          maxLabel="All of it"
          min={0}
          max={ZOOM_STEPS}
          step={1}
          value={zoomForSpanDays(spanDays, historyDays)}
          // Zooming keeps the right-hand edge where it is rather than the
          // middle: the reader is almost always looking at a moment and
          // widening around it, and re-centring would move the thing they
          // were looking at out from under them.
          onChange={(event) => setSpanDays(spanDaysForZoom(Number(event.target.value), historyDays))}
          aria-valuetext={describeSpanDays(spanDays)}
          data-testid="chart-zoom"
          className={styles.zoom}
        />

        {/* The same control for the other axis. Untouched it sits wherever the
            readings put it, so the chart still opens sensibly; the moment it
            is moved the height is the reader's and stops changing under them.
            Only offered once there is a history to have a range over. */}
        {fullSpan > 0 && (
          <Slider
            label="How much range"
            valueLabel={`${(rangeSpan ?? autoRangeSpan).toFixed(axisPrecision)} ${node.unit ?? ''}`}
            minLabel="Tight"
            maxLabel="Everything"
            min={0}
            max={RANGE_STEPS}
            step={1}
            value={zoomForRangeSpan(rangeSpan ?? autoRangeSpan, fullSpan, axisPrecision)}
            onChange={(event) =>
              setChosenSpan(rangeSpanForZoom(Number(event.target.value), fullSpan, axisPrecision))
            }
            aria-valuetext={`${(rangeSpan ?? autoRangeSpan).toFixed(axisPrecision)} ${node.unit ?? ''}`}
            data-testid="chart-range"
            className={styles.zoom}
          />
        )}

        {/* Grouping, for when the zoom's choice is not the reader's — weekly
            averages over a single month are a legitimate thing to want. What
            each direction of the toggle means lives in
            `groupingAfterWeeklyToggle`, beside its test. */}
        {node.entryCount > 0 && (
          <Checkbox
            label="Average per week"
            hint="One point per week, whatever range is on screen."
            checked={resolution === 'weekly'}
            onChange={(event) =>
              setGrouping(groupingAfterWeeklyToggle(event.target.checked, autoResolution))
            }
            data-testid="toggle-weekly"
          />
        )}
        </ChartViewMenu>
      </Stack>

      {state.kind === 'loading' && (
        <Card className={styles.stateCard} data-testid="node-detail-loading">
          <Stack gap={3}>
            <Inline gap={3} align="center">
              <Spinner size="sm" aria-label="Loading readings" />
              <Text size="sm" color="muted">Loading readings…</Text>
            </Inline>
            <Skeleton height="16rem" />
          </Stack>
        </Card>
      )}

      {state.kind === 'error' && (
        <Card className={styles.stateCard} data-testid="node-detail-error">
          <Alert variant="error" title="Couldn't load these readings">{state.message}</Alert>
        </Card>
      )}

      {state.kind === 'empty' && (
        <Card className={styles.stateCard} data-testid="node-detail-empty">
          <Stack gap={2}>
            <Heading level={3} size="lg">No readings yet</Heading>
            <Text size="sm" color="secondary">
              Once {node.title.toLowerCase()} has a measurement, it will show up here.
            </Text>
          </Stack>
        </Card>
      )}

      {state.kind === 'ready' && (
        <Stack gap={4}>
          {/* `.body` turns into a ROW above 64rem, so only the two things
              that belong side by side may live in it. The plan analysis goes
              BELOW that row at full width — dropped inside it, it became a
              third column with no flex rule of its own and fought the chart
              for space, which is exactly how it shipped broken once. */}
          <div className={styles.body}>
          <Card className={styles.chartCard} data-testid="node-detail-chart">
            <MeasurementChart
              series={visibleSeries}
              window={visibleWindow ?? undefined}
              unit={node.unit}
              precision={node.precision}
              aggregated={state.aggregated}
              bucket={state.bucket}
              /* Only when there is history outside the window to reach. The
                 chart claims the gesture (and calls preventDefault) only if
                 this is given, so with nothing to pan to the page keeps the
                 swipe. Positive is forward in time; `offsetDays` counts
                 backwards from the latest reading, hence the subtraction —
                 the same direction the browser's own scroll container went
                 when the chart still lived inside one. */
              onPan={
                panRatio > 1
                  ? (deltaDays) =>
                      setOffsetDays((current) =>
                        clampOffsetDays(current - deltaDays, spanDays, oldestIso, latestIso),
                      )
                  : undefined
              }
              suggestedAxis={suggestedAxis}
              valueAxis={chosenAxis ?? undefined}
              goalThreshold={goal?.targetValue}
              changes={visibleChanges}
              /* An empty array rather than undefined when the layer is off
                 but available: the chart then reserves the label lane without
                 drawing in it, and ticking the box fills space that was
                 already there instead of growing the chart. undefined is for
                 a dashboard with no events node at all. */
              events={eventsNodeId === null ? undefined : overlay.enabled ? overlay.events : []}
            />

            {/* The scrollbar sits BESIDE the chart rather than around it.
                Wrapping the chart in the scroll container was the obvious
                arrangement and the wrong one: a scroll container clips both
                axes, so every tooltip that grew past the chart lost an edge —
                first the popover's bottom, then the reading tooltip's top,
                then the popover's bottom again once its content got longer.
                Out here it scrolls nothing of its own; the spacer gives it a
                thumb, and its scrollLeft is read as the position in time. */}
            {/* Our own bar rather than the browser's.
                The first version was a real scroll container with nothing in
                it but a spacer, and on macOS that is a control you cannot see:
                the system draws its scrollbar only while you are already
                scrolling, and a twelve-pixel strip with nothing else in it
                gives you nothing to start with. A range input is visible at
                rest, is the same control the two sliders above already are,
                and brings its own keyboard handling. */}
            {panRatio > 1 && (
              <input
                type="range"
                className={styles.position}
                min={0}
                max={Math.round(maxOffsetDays)}
                step={1}
                // Reversed: the track reads left to right as the history
                // does, so dragging right walks forward in time, while
                // `offsetDays` counts BACKWARDS from the latest reading.
                value={Math.round(maxOffsetDays - clampedOffset)}
                onChange={(event) => setOffsetDays(maxOffsetDays - Number(event.target.value))}
                aria-label="Position in time"
                aria-valuetext={
                  visibleWindow
                    ? `${formatWindowEdge(visibleWindow.from)} to ${formatWindowEdge(visibleWindow.to)}`
                    : undefined
                }
                data-testid="chart-scroller"
              />
            )}
            {/* Offered only when there is an events node this caller may read.
                On someone else's dashboard that means the events node was
                shared too; when it was not, there is nothing here to tick. */}
            {eventsNodeId !== null && (
              <Checkbox
                label="Show life events"
                hint="Anything worth remembering — laid over this chart."
                checked={overlay.enabled}
                onChange={(event) => overlay.toggle(event.target.checked)}
                data-testid="toggle-life-events"
              />
            )}
            {overlay.error && (
              <Text size="xs" color="muted">
                {overlay.error}
              </Text>
            )}
            {state.chartTruncated && (
              <Text size="xs" color="muted">Showing part of this range — there is more history than fits here.</Text>
            )}
            {goal && <GoalReadout node={node} goal={goal} />}
          </Card>

          <Card padded={false} className={styles.readingsCard} data-testid="node-readings-list">
            <div className={styles.readingsHeader}>
              <Text size="xs" color="muted" className={styles.label}>Measurements</Text>
            </div>
            <div className={styles.readingsBody}>
              {state.readings.map((reading) => (
                <div key={`${reading.recordedAt}-${reading.source ?? ''}`} className={styles.readingRow}>
                  <Stack gap={1}>
                    <Text size="sm">{formatReadingWhen(reading.recordedAt)}</Text>
                    <Text size="xs" color="muted">{sourceLabel(reading.source)}</Text>
                  </Stack>
                  <Text family="figure" tabular size="md">
                    {reading.value.toFixed(node.precision)} {node.unit}
                  </Text>
                </div>
              ))}
            </div>
            {state.readingsTruncated && (
              <div className={styles.readingsFooter}>
                <Text size="xs" color="muted">Older readings are not shown here.</Text>
              </div>
            )}
          </Card>
          </div>

          {plan && (
            <PlanAnalysis
              assessment={plan.assessment}
              points={plan.points}
              trajectories={plan.trajectories}
              unit={node.unit}
              precision={node.precision}
              targetDate={goal?.targetDate ?? null}
              goingDown={plan.goingDown}
              /* Offered only to somebody who HAS a profile to add. A guest on
                 someone else's dashboard has none, and the API already told
                 us so by returning null for a reason they cannot change. */
              onAddProfile={plan.profile === null && !readOnly ? navigateToSettings : undefined}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
}
